import axios from 'axios'
import storage from '../utils/storage'

const BASE_URL = '/api'

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config) => {
  const token = storage.get('pindou_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      storage.remove('pindou_token')
      storage.remove('pindou_user')
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

export const authApi = {
  register: (email, password, username, activationCode, role) => api.post('/auth/register', { email, password, username, activationCode, role }),
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  updateProfile: (username) => api.patch('/auth/profile', { username }),
  changePassword: (currentPassword, newPassword) => api.patch('/auth/password', { currentPassword, newPassword }),
}

export const activationApi = {
  redeem: (code) => api.post('/activation/redeem', { code }),
}

export const inviteApi = {
  list: () => api.get('/invites'),
  create: () => api.post('/invites'),
}

export const paymentApi = {
  createOrder: (productId) => api.post('/payment/create-order', { productId }),
  getOrder: (orderId) => api.get(`/payment/order/${orderId}`),
  getOrders: (page = 1, pageSize = 5) => api.get('/payment/orders', { params: { page, pageSize } }),
}

export const imageApi = {
  generate: (prompt, style = '', width = 512, height = 512, imageUrls = [], styleRefCount = 0, outputMode = 'bead-source') =>
    api.post('/image/generate', { prompt, style, width, height, imageUrls, styleRefCount, outputMode }),
  tips: () => api.get('/image/tips'),
  status: (taskId) => api.get(`/image/status/${taskId}`),
  history: () => api.get('/image/history'),
  upload: (file) => {
    const formData = new FormData()
    formData.append('image', file)
    return api.post('/image/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

export const usageApi = {
  me: () => api.get('/usage/me'),
  export: (payload) => api.post('/usage/export', payload),
  checkin: () => api.post('/usage/checkin'),
  getCheckins: (year, month) => api.get('/usage/checkins', { params: { year, month } }),
}

export const xhsApi = {
  parse: (url) => api.post('/xhs/parse', { url }),
  proxyUrl: (url) => `/api/xhs/proxy?url=${encodeURIComponent(url)}`,
}

export const galleryApi = {
  list: (params = {}) => api.get('/gallery', { params }),
  get: (id) => api.get(`/gallery/${id}`),
  getByShareCode: (code) => api.get(`/gallery/share/${encodeURIComponent(code)}`),
  createShareCode: (id) => api.post(`/gallery/${id}/share`),
  delete: (id) => api.delete(`/gallery/${id}`),
  like: (id) => api.post(`/gallery/${id}/like`),
  setVisibility: (id, isPublic) => api.patch(`/gallery/${id}/visibility`, { isPublic }),
  upload: (file, meta = {}) => {
    const formData = new FormData()
    formData.append('image', file)
    Object.entries(meta).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    return api.post('/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

export const userApi = {
  get: (id) => api.get(`/users/${id}`),
  follow: (id) => api.post(`/users/${id}/follow`),
  getFollowers: (id) => api.get(`/users/${id}/followers`),
  getFollowing: (id) => api.get(`/users/${id}/following`),
}

export const adminApi = {
  generateCodes: (type, days, count) => api.post('/admin/codes/generate', { type, days, count }),
  getCodes: (params = {}) => api.get('/admin/codes', { params }),
  getStats: () => api.get('/admin/stats'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUserPatterns: (id) => api.get(`/admin/users/${id}/patterns`),
  getTips: () => api.get('/admin/settings/tips'),
  saveTips: (tips) => api.put('/admin/settings/tips', { tips }),
}
